// src/features/game/hooks/useGames.ts

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  fetchGamesByStore,
  fetchGamesForToday,
} from '../services/gameService';

import type {
  PlayGame,
} from '../types/game.types';

import {
  getTodayDate,
} from '../../../shared/utils/timeUtils';

export type GameFilterType =
  | 'all'
  | 'running'
  | 'waiting'
  | 'finished';

interface UseGamesParams {
  storeId: number | null;
  selectedDate: string;
  filter: GameFilterType;
  searchTerm: string;
}

export const useGames = ({
  storeId,
  selectedDate,
  filter,
  searchTerm,
}: UseGamesParams) => {
  const [
    games,
    setGames,
  ] = useState<PlayGame[]>([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const loadGames =
    useCallback(
      async (): Promise<void> => {
        if (
          storeId === null
        ) {
          setGames([]);
          return;
        }

        try {
          setLoading(true);

          const today =
            getTodayDate();

          const data =
            selectedDate === today
              ? await fetchGamesForToday(
                  storeId,
                )
              : await fetchGamesByStore(
                  storeId,
                  selectedDate,
                );

          setGames(data);
        } catch (error) {
          console.error(
            '게임 목록 조회 실패:',
            error,
          );

          setGames([]);
        } finally {
          setLoading(false);
        }
      },
      [
        storeId,
        selectedDate,
      ],
    );

  useEffect(() => {
    void loadGames();
  }, [loadGames]);

  const filteredGames =
    useMemo(() => {
      let result = games;

      switch (filter) {
        case 'running':
          result =
            result.filter(
              (game) =>
                game.gameStatus ===
                  'RUNNING' ||
                game.gameStatus ===
                  'STARTED',
            );
          break;

        case 'waiting':
          result =
            result.filter(
              (game) =>
                game.gameStatus ===
                'WAIT',
            );
          break;

        case 'finished':
          result =
            result.filter(
              (game) =>
                game.gameStatus ===
                'FINISHED',
            );
          break;

        default:
          break;
      }

      const search =
        searchTerm
          .trim()
          .toLowerCase();

      if (search) {
        result =
          result.filter(
            (game) =>
              game.gameName
                .toLowerCase()
                .includes(search) ||
              (
                game.gameSubtitle ??
                ''
              )
                .toLowerCase()
                .includes(search),
          );
      }

      return result;
    }, [
      games,
      filter,
      searchTerm,
    ]);

  return {
    games,
    setGames,
    loading,
    loadGames,
    filteredGames,
  };
};